/**
 * reservationChat.ts — 自社チャット（予約者チャット）を LINE ページに並べるための純関数
 *
 * 設計: booking-platform/md/customer-chat.md（Phase 2）／ md/master_md/page-line-chat.md「自社チャットの統合」
 *
 * new-n-car の LP チャット API（chat.php `get_sessions`）が返す会話のうち、予約に結びついた
 * 会話（session_key が `res_` で始まる）だけを LINE ページのチャット・インスタンスと同じ形に変換する。
 * React・fetch に依存しないので、ここに置いた判定はすべて単体テストで固定する。
 */

export const RESERVATION_SESSION_PREFIX = 'res_';
/** LINE ユーザー ID と衝突しない instanceKey / 擬似 line_user_id の接頭辞 */
export const RESV_INSTANCE_PREFIX = 'resv:';

/** chat.php get_sessions の 1 件（必要な項目だけ。他は any で素通し） */
export interface ReservationChatSession {
  session_key: string;
  status?: string | null; // ACTIVE | ARCHIVED
  updated_at?: string | null;
  is_pinned?: number | boolean | null;
  memo?: string | null;
  alert_level?: number | null;
  admin_last_read_id?: number | string | null;
  last_message?: string | null;
  last_sender?: string | null; // user | admin | system
  last_message_id?: number | string | null;
  last_user_at?: string | null;
  admin_handling?: boolean;
  /** 並行実装中（別エージェント）: 無いこともあるので全て任意 */
  contact_code?: string | null;
  reservation_id?: string | number | null;
  resv_customer_name?: string | null;
  /** スタッフが一覧から外した時刻（LINE ページのアーカイブ・2026-10-26）。お客様の新着で LP が null に戻す */
  staff_archived_at?: string | null;
  [key: string]: any;
}

/**
 * 一覧から外れているか: スタッフのアーカイブ（staff_archived_at）か、対応完了（status=ARCHIVED＝お客様にも終了を表示）。
 * 「アーカイブ」タブに出すもの（booking-platform/md/customer-chat.md §13-5）
 */
export const isLpSessionArchived = (s: ReservationChatSession | null | undefined): boolean =>
  Boolean(s && (s.status === 'ARCHIVED' || (typeof s.staff_archived_at === 'string' && s.staff_archived_at)));

/** 画像添付（LP chat_lib/media.php）。URL は署名付き・期限付き（12 時間）。鍵が無いサーバーでは null */
export interface ReservationChatAttachment {
  id: number;
  kind: 'image' | string;
  mime: string;
  width: number;
  height: number;
  bytes: number;
  url: string | null;
  thumb_url: string | null;
  expires_at: number;
}

/** chat.php get_messages の 1 件 */
export interface ReservationChatMessage {
  id: number;
  sender: 'user' | 'admin' | 'system' | string;
  text: string;
  timestamp: string;
  original_text?: string | null;
  original_lang?: string | null;
  translated_text?: string | null;
  translated_lang?: string | null;
  auto_translated?: number | boolean | null;
  /** 画像メッセージ（text は `[画像]`）。無ければ null / undefined */
  attachment?: ReservationChatAttachment | null;
}

/** 送信時翻訳の翻訳先（LP 管理画面 AdminReplyComposer と同じ 4 つ。値は LP の translate / gemini に渡す） */
export const RESV_TRANSLATE_LANGS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'en', label: '英語' },
  { value: 'zh-TW', label: '中国語(繁体)' },
  { value: 'zh', label: '中国語(簡体)' },
  { value: 'ko', label: '韓国語' },
];

/**
 * お客様の言語を推定する: 最後の顧客メッセージの original_lang（LP が受信時に Gemini で検知・保存）→ 翻訳先の候補に正規化。
 * 日本語・不明・候補外は null（スタッフが選ぶ）。
 */
export const detectCustomerLang = (messages: ReservationChatMessage[]): string | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.sender !== 'user') continue;
    const raw = String(m.original_lang || '').toLowerCase().trim();
    if (!raw) continue;
    if (raw === 'ja' || raw === 'jp') return null;
    if (raw === 'zh-tw' || raw === 'zh-hant' || raw === 'tw' || raw === 'zh_tw') return 'zh-TW';
    if (raw === 'zh' || raw === 'zh-cn' || raw === 'zh-hans' || raw === 'cn') return 'zh';
    if (raw === 'ko' || raw === 'kr') return 'ko';
    if (raw === 'en') return 'en';
    return null;
  }
  return null;
};

/** 画像添付の送信前チェック（LP の chatMedia.js と同じ規則）。問題なければ null */
export const RESV_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const validateResvImage = (file: { size?: number; type?: string } | null | undefined): string | null => {
  if (!file) return 'ファイルが選ばれていません';
  if (Number(file.size || 0) > RESV_IMAGE_MAX_BYTES) return '画像が大きすぎます（10MB まで）';
  const type = String(file.type || '').toLowerCase();
  if (type && !type.startsWith('image/')) return '画像ファイル（JPEG / PNG）を選んでください';
  return null;
};

export const isReservationSessionKey = (key: unknown): boolean =>
  typeof key === 'string' && key.startsWith(RESERVATION_SESSION_PREFIX);

export const isReservationInstanceKey = (key: unknown): boolean =>
  typeof key === 'string' && key.startsWith(RESV_INSTANCE_PREFIX);

/** instanceKey（`resv:<session_key>`）から session_key を取り出す。対象外は null */
export const sessionKeyFromInstanceKey = (instanceKey: unknown): string | null =>
  isReservationInstanceKey(instanceKey) ? (instanceKey as string).slice(RESV_INSTANCE_PREFIX.length) : null;

/**
 * スタッフ未読か（LP 管理画面 adminChatStore と同じ規則）:
 * アーカイブ済みでない・最後の発言が顧客・その ID がスタッフ既読位置より新しい。
 */
export const isSessionUnreadForStaff = (s: ReservationChatSession): boolean =>
  s.status !== 'ARCHIVED' &&
  s.last_sender === 'user' &&
  Number(s.last_message_id || 0) > Number(s.admin_last_read_id || 0);

/**
 * 自動メモ「予約 {連絡コード} {氏名}」（new-n-car chat_resv_auto_memo）から連絡コードと氏名を拾う。
 * get_sessions が contact_code / resv_customer_name を返すようになるまでの後方互換。
 */
export const parseReservationMemo = (memo: unknown): { contact_code: string | null; customer_name: string | null } => {
  if (typeof memo !== 'string') return { contact_code: null, customer_name: null };
  const m = memo.trim().match(/^予約\s+(\S+)(?:\s+(.+))?$/);
  if (!m) return { contact_code: null, customer_name: null };
  return { contact_code: m[1] || null, customer_name: (m[2] || '').trim() || null };
};

/** 会話の連絡コード（API の項目 → メモの後方互換 → null） */
export const sessionContactCode = (s: ReservationChatSession): string | null => {
  const direct = typeof s.contact_code === 'string' ? s.contact_code.trim() : '';
  if (direct) return direct;
  return parseReservationMemo(s.memo).contact_code;
};

/**
 * 表示名。`{氏名} 様` ＞ 予約データの氏名 ＞ メモの氏名 ＞ 連絡コード ＞ session_key の末尾。
 * 氏名が取れない間（並行実装のデプロイ前）でも空にならないことをテストで固定する。
 */
export const sessionDisplayName = (s: ReservationChatSession, resCustomerName?: string | null): string => {
  const name = (typeof s.resv_customer_name === 'string' && s.resv_customer_name.trim())
    || (typeof resCustomerName === 'string' && resCustomerName.trim())
    || parseReservationMemo(s.memo).customer_name
    || '';
  if (name) return `${name} 様`;
  const code = sessionContactCode(s);
  if (code) return `予約 ${code}`;
  return `予約者チャット ${String(s.session_key).slice(-6)}`;
};

export interface ReservationLike {
  id?: string | number;
  customer_name?: string | null;
  contact_code?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  pickup_date?: string | null;
  return_date?: string | null;
  status?: string | null;
  status_code?: string | null;
  [key: string]: any;
}

/**
 * LINE ページのカード／フィルタ（useLineFilters）が読む形に変換する。
 * LINE のインスタンスと同じキー名を使うことで、カード描画・検索・日付フィルタをそのまま流用する。
 *
 * @param findReservation 予約 ID → reservationSyncService の予約（無ければ undefined）
 * @param todayStr / tomorrowStr 'YYYY-MM-DD'（LINE 側のバッジ計算と同じ）
 */
export function toReservationChatInstance(
  s: ReservationChatSession,
  findReservation: (id: string | number) => ReservationLike | undefined,
  todayStr: string,
  tomorrowStr: string,
): Record<string, any> {
  const rid = s.reservation_id != null && String(s.reservation_id) !== '' ? String(s.reservation_id) : null;
  const raw = rid ? findReservation(rid) : undefined;
  const resData: ReservationLike | null = raw
    ? {
        ...raw,
        // LINE カードは pickup_date / return_date を見る。予約データは start_date / end_date なので別名を足す
        pickup_date: raw.pickup_date || raw.start_date || null,
        return_date: raw.return_date || raw.end_date || null,
        contact_code: raw.contact_code || sessionContactCode(s),
      }
    : null;

  const badges: string[] = [];
  if (resData) {
    const status = resData.status || resData.status_code;
    const pDate = String(resData.start_date || '').split(' ')[0].split('T')[0];
    const rDate = String(resData.end_date || '').split(' ')[0].split('T')[0];
    if (status === 'in_use' || status === 'in-use' || status === '利用中') badges.push('利用中');
    if (pDate === todayStr) badges.push('本日貸出');
    if (rDate === todayStr) badges.push('本日返却');
    if (pDate === tomorrowStr) badges.push('明日貸出');
    if (rDate === tomorrowStr) badges.push('明日返却');
  }

  const displayName = sessionDisplayName(s, resData?.customer_name ?? null);
  const contactCode = sessionContactCode(s) || resData?.contact_code || null;
  const unread = isSessionUnreadForStaff(s);

  return {
    kind: 'resv',
    session_key: s.session_key,
    instanceKey: `${RESV_INSTANCE_PREFIX}${s.session_key}`,
    // LINE 用コード（検索・既読・通知音の id 比較）が参照するので衝突しない擬似 ID を入れる
    line_user_id: `${RESV_INSTANCE_PREFIX}${s.session_key}`,
    line_display_name: displayName,
    custom_display_name: displayName,
    line_picture_url: null,
    contact_code: contactCode,
    // 未読数は API が返さない（最後の発言 ID と既読位置の比較のみ）。1 = 「新着あり」の意味で使う
    unread_count: unread ? 1 : 0,
    // 並び・表示の時刻は最後のメッセージ（LP の last_message_at）。updated_at は ON UPDATE で書き換わるので最後の手段
    last_message_at: (typeof s.last_message_at === 'string' && s.last_message_at) || s.updated_at || s.last_user_at || null,
    last_message_content: s.last_message || '',
    last_sender: s.last_sender || null,
    is_pinned: Boolean(Number(s.is_pinned || 0)),
    is_archived: isLpSessionArchived(s),
    staff_archived: Boolean(s.staff_archived_at),
    chat_closed: s.status === 'ARCHIVED',
    alert_level: Number(s.alert_level || 0),
    admin_handling: Boolean(s.admin_handling),
    memo: s.memo || '',
    tags: [],
    reservation_ids: rid ? [rid] : [],
    linked_reservations: rid ? [{ reservation_id: rid, contact_code: contactCode }] : [],
    instance_reservation_id: rid,
    instance_res_data: resData,
    hasOtherReservations: false,
    reservation_statuses: badges,
  };
}

/**
 * get_sessions の結果から LINE ページに並べる会話を選ぶ。
 * - 予約者チャット（`res_`）だけ。匿名の LP チャットは従来どおり LP 管理画面で扱う
 * - アーカイブ済みは showArchived のときだけ
 */
export const selectReservationSessions = (
  sessions: ReservationChatSession[] | null | undefined,
  showArchived: boolean,
): ReservationChatSession[] =>
  (Array.isArray(sessions) ? sessions : []).filter(
    (s) => isReservationSessionKey(s?.session_key) && (showArchived || !isLpSessionArchived(s)),
  );

/** 並べ替えキー（新しい順）。文字列は `YYYY-MM-DD HH:MM:SS` / ISO のどちらも受ける */
const toTime = (v: unknown): number => {
  if (typeof v !== 'string' || !v) return 0;
  const t = new Date(v.includes('T') ? v : v.replace(/-/g, '/')).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * 一覧の並び（2026-10-30 オーナー決定・booking-platform/md/customer-chat.md §13-7）:
 * **未読 → ピン留め → その他**。未読の中ではピン留めを先に、残りは最終メッセージの新しい順。
 * ピン留めで未読の会話は「未読」のグループに入る（未読を先に見る）。
 * 旧: ピン留め → 未読 → 新しい順（LINE の get_users の並び）
 */
export function compareBoardOrder(
  a: { is_pinned?: any; last_message_at?: any },
  b: { is_pinned?: any; last_message_at?: any },
  unreadOf: (x: any) => boolean,
  timeOf: (x: any) => number,
): number {
  const unread = Number(unreadOf(b)) - Number(unreadOf(a));
  if (unread !== 0) return unread;
  const pin = Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned));
  if (pin !== 0) return pin;
  return timeOf(b) - timeOf(a);
}

/**
 * LINE と自社チャットを 1 つの一覧に混ぜ、並びを揃える（未読 → ピン留め → 新しい順・compareBoardOrder）。
 * 自社チャットが無いときも並べ直す（LINE の get_users はピン留めが先に来るため）。
 */
export function mergeChatInstances<T extends Record<string, any>>(lineInstances: T[], resvInstances: T[]): T[] {
  return [...lineInstances, ...(resvInstances || [])].sort((a, b) =>
    compareBoardOrder(a, b, (x) => (x.unread_count || 0) > 0, (x) => toTime(x.last_message_at)),
  );
}

/** 送信・既読の Bearer（この店舗の backend の Sanctum トークン = r-cal のログイン） */
export const chatAuthHeaders = (token: string | null): Record<string, string> => {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

/** 招待リンクの期限（UNIX 秒）を `M/D HH:MM まで` に。0・不正は空文字 */
export const formatInviteExpiry = (expiresAt: number | string | null | undefined): string => {
  const sec = Number(expiresAt || 0);
  if (!Number.isFinite(sec) || sec <= 0) return '';
  const d = new Date(sec * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())} まで`;
};

/**
 * 招待リンクをお客様に LINE で送るときの文面（入力欄に入れるだけ。自動送信はしない）。
 * 「1回限り」「期限」を必ず含める。URL は末尾に置き、改行で区切る（LINE でリンクが切れないように）。
 */
export const buildInviteLineMessage = (invite: { url: string; expires_at: number | string | null | undefined }, storeName?: string | null): string => {
  const exp = formatInviteExpiry(invite.expires_at);
  const who = storeName ? `${storeName}です。` : '';
  return [
    `${who}ご予約に関するご連絡は、下記のリンクから「予約者チャット」でお送りいただけます。`,
    `※ このリンクは1回限り（${exp || '期限付き'}）有効です。開いた端末でそのままご利用になれます。`,
    invite.url,
  ].join('\n');
};

/** 既読位置より新しい顧客メッセージが何件あるか（パネルを開いたときの表示用） */
export const countUnreadMessages = (messages: ReservationChatMessage[], adminLastReadId: number | string | null | undefined): number =>
  messages.filter((m) => m.sender === 'user' && Number(m.id) > Number(adminLastReadId || 0)).length;

/**
 * 予約 ID → その予約の予約チャット（`res_`）。daylist の予約行から自社チャットを開くため（2026-10-30・customer-chat.md §13-7）。
 * 1 予約 1 会話が原則。万一複数あれば最後のメッセージが新しい方。予約チャット以外（匿名）は入れない
 * （予約前の匿名の会話は予約チャットのパネルから辿る）。
 */
export function reservationChatByReservation(sessions: ReservationChatSession[] | null | undefined): Record<string, ReservationChatSession> {
  const out: Record<string, ReservationChatSession> = {};
  (Array.isArray(sessions) ? sessions : []).forEach((s) => {
    if (!s || !isReservationSessionKey(s.session_key) || s.reservation_id == null || String(s.reservation_id) === '') return;
    const rid = String(s.reservation_id);
    const cur = out[rid];
    const t = (x: ReservationChatSession) => toTime((typeof x.last_message_at === 'string' && x.last_message_at) || x.updated_at);
    if (!cur || t(s) > t(cur)) out[rid] = s;
  });
  return out;
}
